import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as CriticalAssetColorIcon } from '@elementary/common-ui/assets/16x16/critical-asset-tag-color.svg';
import { ReactComponent as DownloadIcon } from '@elementary/common-ui/assets/download.svg';
import { Button, Flex, FlexColumn } from '@elementary/common-ui/components';
import Table, {
  BaseTableProps,
  DisplayPanel,
  GroupedTable,
  QuickFilter,
  TableFilters,
  TableEmptyState,
} from '@elementary/common-ui/components/Table';
import { CATALOG_TABLE_FILTERS } from '@elementary/common-ui/constants/urlParams';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import useDebounce from '@elementary/common-ui/hooks/useDebounce';
import Humanize from 'humanize-plus';
import AssetsTableActionMenu from 'shared/components/AssetsTableActionMenu';
import { EDITABLE_ASSET_TYPES } from 'shared/constants';
import useChatContext from '@elementary/common-ui/hooks/useChatContext';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import BulkEditWizard from '../BulkEditWizard';
import { BulkEditProperty } from '../BulkEditWizard/types';
import { CatalogFilterOptions } from '../types';
import useCatalogSidePanel from '../../hooks/useCatalogSidePanel';
import {
  useCatalogTableColumns,
  useCatalogTableData,
  useGroupedCatalogTableData,
} from './hooks';
import { CatalogTableAssetData } from './types';

const TableWrapper = styled(FlexColumn)`
  position: relative;
  background-color: ${({ theme }) => theme.palette.common.white};
  overflow: hidden;
  width: 100%;
  height: 100%;
  gap: 24px;
`;

const TableHeader = styled(Flex)`
  gap: 24px;
  padding-bottom: 16px;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
`;

const localStorageKey = 'catalog_table';

const CatalogTable = () => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [excludedItems, setExcludedItems] = useState<string[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [bulkEditWizardEditProperty, setBulkEditWizardEditProperty] = useState<
    BulkEditProperty | undefined
  >();
  const [filteredTableData, setFilteredTableData] = useState<
    CatalogTableAssetData[]
  >([]);

  const { treeType } = useTreeViewParams();
  const { openSidePanel, closeSidePanel, selectedId } = useSidePanelParams();
  useCatalogSidePanel();
  const hasPermission = useEnvPermission();
  const canEdit = hasPermission('EDIT_ASSETS');
  const grouped = useGroupedCatalogTableData({ search: debouncedSearch });
  const { isGrouped } = grouped;
  const {
    data: flatData,
    filterOptions,
    onCSVDownload,
    isExporting,
    isAssetsFetching,
    isFilterOptionsFetching,
    fetchNextPage,
    hasNextPage,
  } = useCatalogTableData({
    search: debouncedSearch,
    enabled: !isGrouped,
  });
  const data = isGrouped ? grouped.data : flatData;
  const hasResults = isGrouped ? grouped.hasResults : flatData.length > 0;
  const tableColumns = useCatalogTableColumns({ data, filterOptions });

  const selectedIds = isSelectAll
    ? excludedItems
      ? filteredTableData
          .map((model) => model.id)
          .filter((id) => !excludedItems.includes(id))
      : 'all'
    : selectedItems;

  const selectedModels =
    selectedIds === 'all'
      ? filteredTableData
      : filteredTableData.filter((model) => selectedIds.includes(model.id));

  const chatContext = useMemo(() => {
    if (selectedModels.length === 0) return undefined;
    return {
      assetIds: selectedModels.map((m) => m.id),
      displayLabels: selectedModels.map((m) => m.name),
    };
  }, [selectedModels]);

  useChatContext(chatContext);

  const onScrollToBottom = useCallback(() => {
    if (fetchNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const filterCounter = useMemo(() => {
    const criticalAssetsFilterOptions = filterOptions['isCritical'];
    const artifactTypeFilterOptions = filterOptions['artifactType'];
    const all =
      criticalAssetsFilterOptions?.reduce(
        (acc, curr) => acc + (curr.count || 0),
        0,
      ) ?? 0;
    const isCriticalCount =
      criticalAssetsFilterOptions?.find((option) => option.value === 'true')
        ?.count || 0;
    const exposureCount =
      artifactTypeFilterOptions?.find((option) => option.value === 'exposure')
        ?.count || 0;
    return {
      [CatalogFilterOptions.All]: all,
      [CatalogFilterOptions.CriticalAssets]: isCriticalCount,
      [CatalogFilterOptions.BIAssets]: exposureCount,
    };
  }, [filterOptions]);

  const quickFilters: QuickFilter[] = [
    treeType !== 'bi_assets' && {
      name: isFilterOptionsFetching
        ? 'Exposures'
        : `Exposures (${Humanize.intComma(
            filterCounter[CatalogFilterOptions.BIAssets],
          )})`,
      filters: [
        {
          property: 'artifactType',
          operator: 'equals',
          value: ['exposure'],
        },
      ],
      loading: isFilterOptionsFetching,
    },
    {
      name: isFilterOptionsFetching
        ? 'Critical assets'
        : `Critical assets (${Humanize.intComma(
            filterCounter[CatalogFilterOptions.CriticalAssets],
          )})`,
      loading: isFilterOptionsFetching,
      icon: <CriticalAssetColorIcon width={20} />,
      filters: [
        {
          property: 'isCritical',
          operator: 'equals',
          value: ['true'],
        },
      ],
    },
  ].filter(Boolean) as QuickFilter[];

  const clearSelections = () => {
    setSelectedItems([]);
    setExcludedItems([]);
    setIsSelectAll(false);
    setBulkEditWizardEditProperty(undefined);
  };

  const onGroupByChange = (groupBy: string | null) => {
    clearSelections();
    grouped.onGroupByChange(groupBy);
  };

  useEffect(() => {
    if (selectedIds.length && selectedIds !== 'all') {
      const isSelectedIdsInTable = selectedIds.every((id) =>
        filteredTableData.find((tableItem) => tableItem.id === id),
      );

      if (!isSelectedIdsInTable) {
        clearSelections();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filteredTableData]);

  const isLoading = isGrouped ? grouped.isFetching : isAssetsFetching;

  useViewLoadTracking({
    name: 'Catalog table data',
    loadDeps: [(hasResults && !isLoading) || null],
  });

  const commonTableProps: BaseTableProps<CatalogTableAssetData> = {
    wrapperSx: {
      height: '100%',
      maxHeight: '100%',
    },
    idKey: 'id',
    columns: tableColumns,
    onRowClick: ({ id }) =>
      id === selectedId ? closeSidePanel() : openSidePanel('asset', id),
    isLoading,
    localStorageKey,
    urlParamsPrefix: CATALOG_TABLE_FILTERS,
    search,
    selectedItems,
    isSelectAll,
    excludedItems,
    onSelectItem: setSelectedItems,
    onExcludeItem: setExcludedItems,
    onSelectAllChange: setIsSelectAll,
    emptyStateRenderer: TableEmptyState,
    onFilteredItemsChange: setFilteredTableData,
    selectionColumn: canEdit,
    focusedRows: selectedId ? [selectedId] : [],
    isFilterOptionsLoading: isFilterOptionsFetching,
    bottomScrollPadding: 100,
  };

  return (
    <TableWrapper>
      <TableHeader>
        <Flex gap="24px">
          <TableFilters
            data={data}
            columns={tableColumns}
            urlParamsPrefix={CATALOG_TABLE_FILTERS}
            search={search}
            onSearch={setSearch}
            quickFilters={quickFilters}
            clearFiltersChipLabel={
              isFilterOptionsFetching
                ? 'All'
                : `All (${Humanize.intComma(
                    filterCounter[CatalogFilterOptions.All],
                  )})`
            }
            isClearFiltersLoading={isFilterOptionsFetching}
            disabled={isLoading}
          />
        </Flex>

        <Flex sx={{ gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onCSVDownload}
            startIcon={<DownloadIcon width={12} />}
            disabled={!hasResults || isLoading || isExporting}
          >
            {isExporting ? 'Exporting...' : 'CSV'}
          </Button>
          <DisplayPanel
            columns={tableColumns}
            localStorageKey={localStorageKey}
            groupBy={grouped.groupBy}
            onGroupByChange={onGroupByChange}
            groupableColumnIds={grouped.groupableColumnIds}
          />
        </Flex>
      </TableHeader>

      {grouped.groupFetchers}
      {isGrouped ? (
        <GroupedTable
          {...commonTableProps}
          data={grouped.groupedData}
          expandedGroups={grouped.expandedGroups}
          onGroupToggle={grouped.onGroupToggle}
          onGroupScrollToBottom={grouped.onGroupScrollToBottom}
          showEmptyGroups={false}
          isControlled
        />
      ) : (
        <Table
          {...commonTableProps}
          data={flatData}
          onScrollToBottom={onScrollToBottom}
          isControlled={!!fetchNextPage}
        />
      )}

      <AssetsTableActionMenu
        selectedAssets={selectedModels.map((model) => model.id)}
        onOpenWizard={(value) => setBulkEditWizardEditProperty(value)}
        isCriticalAssetsLoading={isFilterOptionsFetching}
        isEditable={selectedModels?.every((model) =>
          EDITABLE_ASSET_TYPES.includes(model.assetType),
        )}
        onClose={clearSelections}
      />

      <BulkEditWizard
        isOpen={!!bulkEditWizardEditProperty}
        editProperty={bulkEditWizardEditProperty}
        onClose={() => setBulkEditWizardEditProperty(undefined)}
        onSubmit={clearSelections}
        modelIds={selectedModels.map((m) => m.id)}
      />
    </TableWrapper>
  );
};

export default CatalogTable;
